import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Building2, CheckCircle, Mail, XCircle } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { runBootstrap } from '@/features/app-state';
import { logoutUser } from '@/features/auth';
import {
  acceptInvitation,
  clearActiveInvitationContext,
  selectActiveInvitation,
  selectActiveInvitationErrorMessage,
  selectActiveInvitationErrorReason,
  selectActiveInvitationPhase,
  selectActiveInvitationToken,
  setActiveInvitationToken,
  verifyInvitationToken,
} from '@/features/invitations';
import { setActiveOrganizationId } from '@/features/organizations/organizationsSlice';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

type AcceptStep = 'submitting' | 'complete';

function normalizeEmail(value?: string | null): string {
  return value?.trim().toLowerCase() || '';
}

export default function AcceptInvitationPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromQuery = searchParams.get('token');

  const signedInEmail = useAppSelector((state) => state.auth.email);
  const token = useAppSelector(selectActiveInvitationToken);
  const effectiveToken = tokenFromQuery ?? token;
  const invitationData = useAppSelector(selectActiveInvitation);
  const invitationPhase = useAppSelector(selectActiveInvitationPhase);
  const errorReason = useAppSelector(selectActiveInvitationErrorReason);
  const errorMessage = useAppSelector(selectActiveInvitationErrorMessage);
  const [step, setStep] = useState<AcceptStep | null>(null);

  useEffect(() => {
    if (tokenFromQuery) {
      if (tokenFromQuery !== token) {
        dispatch(setActiveInvitationToken(tokenFromQuery));
        void dispatch(verifyInvitationToken(tokenFromQuery));
        return;
      }

      if (invitationPhase === 'idle') {
        void dispatch(verifyInvitationToken(tokenFromQuery));
      }
      return;
    }

    if (token && invitationPhase === 'idle') {
      void dispatch(verifyInvitationToken(token));
    }
  }, [dispatch, invitationPhase, token, tokenFromQuery]);

  const invitedEmail = normalizeEmail(invitationData?.email);
  const currentEmail = normalizeEmail(signedInEmail);
  const emailMismatch = Boolean(invitedEmail && currentEmail && invitedEmail !== currentEmail);

  const handleAcceptInvitation = async () => {
    const invitationToken = effectiveToken?.trim();
    if (!invitationToken) return;

    try {
      setStep('submitting');
      const response = await dispatch(
        acceptInvitation({
          token: invitationToken,
        }),
      ).unwrap();

      await dispatch(runBootstrap()).unwrap();
      if (response.organizationID) {
        dispatch(setActiveOrganizationId(response.organizationID));
      }
      dispatch(clearActiveInvitationContext());
      setStep('complete');
      toast.success('Invitation accepted');
      navigate('/', { replace: true });
    } catch (error) {
      const message = error && typeof error === 'object' && 'message' in error ? String(error.message) : null;
      toast.error(message || 'Failed to accept invitation');
      setStep(null);
    }
  };

  const handleSwitchAccount = async () => {
    await dispatch(logoutUser()).unwrap();
    navigate('/login', { replace: true });
  };

  if ((effectiveToken && invitationPhase === 'idle') || invitationPhase === 'verifying') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <LoadingSpinner size={80} />
        <p className="text-muted-foreground">Verifying invitation…</p>
      </div>
    );
  }

  if (!effectiveToken || invitationPhase === 'error' || !invitationData) {
    const errorConfig = {
      expired: {
        icon: <XCircle className="text-destructive h-12 w-12" />,
        title: 'Invitation Expired',
        description: 'This invitation link has expired. Please contact your administrator for a new invitation.',
      },
      accepted: {
        icon: <CheckCircle className="h-12 w-12 text-green-500" />,
        title: 'Already Accepted',
        description: 'This invitation has already been accepted.',
      },
      revoked: {
        icon: <AlertCircle className="h-12 w-12 text-yellow-500" />,
        title: 'Invitation Cancelled',
        description: 'This invitation has been cancelled. Please contact your administrator.',
      },
      not_found: {
        icon: <XCircle className="text-destructive h-12 w-12" />,
        title: 'Invalid Invitation',
        description: 'This invitation link is invalid. Please check the link and try again.',
      },
    };

    const config = errorConfig[errorReason as keyof typeof errorConfig] || errorConfig.not_found;
    const description = errorMessage || config.description;

    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mb-4 flex justify-center">{config.icon}</div>
            <CardTitle>{config.title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" asChild className="w-full">
              <Link to="/">Back to Home</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Accept Invitation</CardTitle>
          <CardDescription>
            {invitationData?.inviterName
              ? `${invitationData.inviterName} invited you to join ${invitationData?.orgName}`
              : `Join ${invitationData?.orgName}`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <div className="bg-muted/50 flex items-center gap-3 rounded-lg p-3">
              <Mail className="text-muted-foreground h-5 w-5" />
              <div className="flex-1">
                <p className="text-muted-foreground text-xs">Signed in as</p>
                <p className="text-sm font-medium">{signedInEmail || 'Unknown account'}</p>
              </div>
            </div>
            <div className="bg-muted/50 flex items-center gap-3 rounded-lg p-3">
              <Building2 className="text-muted-foreground h-5 w-5" />
              <div className="flex-1">
                <p className="text-muted-foreground text-xs">Organization</p>
                <p className="text-sm font-medium">{invitationData?.orgName}</p>
              </div>
            </div>
          </div>

          {emailMismatch ? (
            <div className="space-y-3">
              <div className="bg-destructive/10 border-destructive/25 text-destructive rounded-md border px-3 py-2">
                <p className="text-sm font-medium">Wrong signed-in account</p>
                <p className="mt-1 text-sm">
                  This invitation was sent to {invitationData?.email}. Sign in with that account to accept it.
                </p>
              </div>
              <Button className="w-full" onClick={handleSwitchAccount}>
                Sign out and switch account
              </Button>
            </div>
          ) : (
            <Button className="w-full" onClick={handleAcceptInvitation} disabled={step === 'submitting'}>
              {step === 'submitting' ? 'Accepting…' : 'Accept Invitation'}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
